import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addDays, format } from 'date-fns';
import { toDate, toZonedTime } from 'date-fns-tz';
import { ModalButtonWithForm, TargetSchedule, User } from 'components/common';

import type { PathStep, UserWithBasicData } from 'shared-types';

interface Props {
  step: Pick<
    PathStep,
    | 'id'
    | 'deadlineDate'
    | 'deadlineDenyAccess'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
  >;
  targetUser: UserWithBasicData;
  updateUsers: () => Promise<void>;
  userSettings: Pick<
    PathStep,
    | 'deadlineDate'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
  >;
}

const PathStepParticipantsEdit = ({
  step,
  targetUser,
  updateUsers,
  userSettings
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { path },
    serverConfig: { timeZone }
  } = useReduxState();

  const handleSubmit = async (values: typeof initialValues) => {
    const response = await api.put(
      `/admin/paths/${path.id}/steps/${step.id}/users/${targetUser.id}`,
      {
        ...values,
        deadlineType:
          values.deadlineType !== 'none' ? values.deadlineType : null,
        deadlineDate: values.deadlineDate
          ? toDate(`${format(values.deadlineDate, 'y-MM-dd')}T23:59:59`, {
              timeZone
            })
          : null
      }
    );

    if (!response.data && !response.data.success) return false;

    await updateUsers();

    dispatch.addNotice({
      text: messages.settingsSavedSuccessfully,
      type: 'success'
    });
    return true;
  };

  const initialValues = {
    deadlineDate: userSettings?.deadlineDate
      ? Date.parse(toZonedTime(userSettings.deadlineDate, timeZone).toString())
      : Date.parse(addDays(dateNow, 1).toString()),
    deadlineDurationUnit: userSettings?.deadlineDurationUnit || 'days',
    deadlineDurationValue: userSettings?.deadlineDurationValue || 7,
    deadlineType: userSettings?.deadlineType
      ? userSettings.deadlineType
      : 'course-default'
  };

  return (
    <ModalButtonWithForm
      buttonIcon="pen"
      buttonText={messages.commonEdit}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalTitle={messages.pathsUsersEditSettings}
      onSubmit={handleSubmit}
    >
      {() => (
        <div className="content">
          <div className="user">
            <User enableProfileLink={true} user={targetUser} />
          </div>

          <TargetSchedule
            settings={step}
            id={`${step.id}-${targetUser.id}`}
            includeCourseDefaultOption={true}
          />
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default PathStepParticipantsEdit;
